import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router';
import type { ReactNode } from 'react';

import { createApiClient } from '@/api/client';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { ErrorScreen } from '@/components/ErrorScreen';
import { LoadingScreen } from '@/components/LoadingScreen';
import {
  createProfileService,
  isProfileComplete,
} from '@/features/profile/profileService';
import type { ProfileData } from '@/features/profile/profileTypes';
import { profileQueryKey } from '@/features/profile/profileTypes';
import { withReturnTo } from '@/routes/returnTo';

import { useAuth } from './useAuth';

interface ProfileCompletionRouteProps {
  children: ReactNode;
}

function getCurrentReturnTo(location: ReturnType<typeof useLocation>) {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function ProfileCompletionRoute({
  children,
}: ProfileCompletionRouteProps) {
  const { getIdToken, loading, signOut, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const kuduUserId = getKuduUserId(user) ?? user?.uid ?? null;
  const returnTo = getCurrentReturnTo(location);

  const profileQuery = useQuery<ProfileData, Error>({
    enabled: !loading && Boolean(user) && Boolean(kuduUserId),
    queryFn: async () => {
      if (!user || !kuduUserId) {
        throw new Error('You must be signed in to view this page.');
      }

      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: async () => {
          await signOut();
          navigate(withReturnTo('/login', returnTo), { replace: true });
        },
      });

      return createProfileService(client).getProfile({ userId: kuduUserId });
    },
    queryKey: profileQueryKey(kuduUserId),
    retry: false,
  });

  if (loading || (Boolean(kuduUserId) && profileQuery.isPending)) {
    return <LoadingScreen message="Checking your profile..." />;
  }

  if (profileQuery.error) {
    return (
      <ErrorScreen
        message="Unable to verify your profile."
        onRetry={() => {
          void profileQuery.refetch();
        }}
      />
    );
  }

  if (!isProfileComplete(profileQuery.data)) {
    return (
      <Navigate
        replace
        to={withReturnTo('/profile/setup', returnTo)}
      />
    );
  }

  return <>{children}</>;
}
